import { Globe, Brain, Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import PestelChecklist from './PestelChecklist';
import { useState, useCallback, useEffect } from 'react';
import {
  calculatePestelChallenge,
  calculateExternalChallenge,
} from '../../../services/scoringService';
import { companiesApi } from '../../../services/api';

// AI Analysis Animation Component
const AIAnalysisIndicator = ({ isAnalyzing }: { isAnalyzing: boolean }) => {
  if (!isAnalyzing) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      >
        <Brain className="h-4 w-4" />
      </motion.div>
      <span className="text-sm font-medium">Analyzing with AI</span>
      <motion.div
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <Wand2 className="h-3 w-3" />
      </motion.div>
    </motion.div>
  );
};

interface PestelSectionProps {
  companyId: string;
  onProgressChange?: (progress: number) => void;
}

// Functions replaced by centralized scoring service

const pestelCategories = [
  {
    key: 'political',
    title: 'Political',
    description: 'Government policies and political stability',
    entityType: 'pestel.political',
  },
  {
    key: 'economic',
    title: 'Economic',
    description: 'Economic growth and financial factors',
    entityType: 'pestel.economic',
  },
  {
    key: 'sociocultural',
    title: 'Sociocultural',
    description: 'Cultural trends and demographic changes',
    entityType: 'pestel.sociocultural',
  },
  {
    key: 'technological',
    title: 'Technological',
    description: 'Technology advances and innovation',
    entityType: 'pestel.technological',
  },
  {
    key: 'environmental',
    title: 'Environmental',
    description: 'Climate and environmental regulations',
    entityType: 'pestel.environmental',
  },
  {
    key: 'legal',
    title: 'Legal',
    description: 'Laws and regulatory compliance',
    entityType: 'pestel.legal',
  },
];

export default function PestelSection({ companyId, onProgressChange }: PestelSectionProps) {
  const [activeCategory, setActiveCategory] = useState('political');
  const [categoryScores, setCategoryScores] = useState<Record<string, number>>({});
  const [backendExternalChallenge, setBackendExternalChallenge] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastChangeTime, setLastChangeTime] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Calculate and report progress based on completed categories
  const calculateProgress = useCallback(() => {
    // Calculate progress based on how many categories have been analyzed (have responses)
    const totalCategories = 6; // PESTEL has 6 categories
    const analyzedCategories = Object.values(categoryScores).filter((score) => score >= 0);

    return Math.round((analyzedCategories.length / totalCategories) * 100);
  }, [categoryScores]);

  // Report initial progress when component mounts or scores change
  useEffect(() => {
    if (onProgressChange) {
      const progress = calculateProgress();
      onProgressChange(progress);
    }
  }, [categoryScores, calculateProgress]); // Removed onProgressChange from deps - it's memoized with useCallback

  const handleScoreChange = useCallback(
    (entityKey: string, score: number) => {
      setCategoryScores((prev) => ({
        ...prev,
        [entityKey]: score,
      }));

      // Only trigger AI analysis animation for user changes, not initial data loading
      if (!isInitialLoad) {
        setIsAnalyzing(true);
        setLastChangeTime(Date.now());
      }
    },
    [isInitialLoad]
  );

  // Fetch backend external challenge to ensure consistency with summary page
  const fetchBackendExternalChallenge = useCallback(async () => {
    try {
      const response = await companiesApi.getSummaryAnalysis(companyId);
      if (response.success && response.data) {
        const summaryData = response.data as any;
        if (summaryData.indicators?.externalChallenge) {
          setBackendExternalChallenge(summaryData.indicators.externalChallenge);
        }
      }
    } catch (error) {
      console.error('Failed to fetch backend external challenge:', error);
    }
  }, [companyId]);

  // Fetch backend data on initial mount
  useEffect(() => {
    fetchBackendExternalChallenge();
  }, [fetchBackendExternalChallenge]);

  // Set initial load to false after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000); // Wait 1 second to allow initial data to load

    return () => clearTimeout(timer);
  }, []);

  // Auto-refresh metrics after 3-second AI analysis animation
  useEffect(() => {
    if (lastChangeTime > 0) {
      // Stop analyzing animation after exactly 3 seconds
      const animationTimer = setTimeout(() => {
        setIsAnalyzing(false);
        // Fetch updated data immediately after animation ends
        fetchBackendExternalChallenge();
      }, 3000); // 3 seconds of AI analysis animation

      return () => clearTimeout(animationTimer);
    }
  }, [lastChangeTime, fetchBackendExternalChallenge]);

  return (
    <div className="strategy-framework-section">
      {/* Summary Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="strategy-framework-summary"
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-600">External Challenge:</span>
              {isAnalyzing ? (
                <AIAnalysisIndicator isAnalyzing={true} />
              ) : (
                (() => {
                  // Check if any categories have been analyzed (score >= 0)
                  const validScores = Object.values(categoryScores).filter((score) => score >= 0);

                  // If no categories have been analyzed, show "Not Analyzed"
                  if (validScores.length === 0) {
                    return (
                      <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                        Not Analyzed
                      </span>
                    );
                  }

                  // Use backend result if available, otherwise fall back to frontend calculation
                  let externalChallengeResult;

                  if (backendExternalChallenge) {
                    // Use backend result for consistency with summary page
                    externalChallengeResult = backendExternalChallenge;
                  } else {
                    // Fall back to frontend calculation
                    externalChallengeResult = calculateExternalChallenge(validScores);
                  }

                  return (
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${externalChallengeResult.color}`}
                    >
                      {externalChallengeResult.level}
                    </span>
                  );
                })()
              )}
            </div>
            <div className="text-xs text-gray-500">
              {(() => {
                const validScores = Object.values(categoryScores).filter((score) => score >= 0);
                return `${validScores.length}/6 factors analyzed`;
              })()}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Two Column Layout - Categories Left, Wider Checklist Right */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="strategy-framework-grid"
      >
        {/* Left Side - Category Navigation */}
        <div className="strategy-framework-sidebar flex flex-col space-y-4">
          {/* Category Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center mb-4">
              <Globe className="h-5 w-5 text-green-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">PESTEL Categories</h2>
              <div className="ml-auto text-right">
                <div className="text-sm text-gray-500">Select a category</div>
              </div>
            </div>

            <div className="space-y-2">
              {pestelCategories.map((category) => {
                const categoryScore = categoryScores[category.key] ?? -1;
                const challengeResult = calculatePestelChallenge(categoryScore);
                const hasProgress = categoryScore >= 0;

                return (
                  <button
                    key={category.key}
                    onClick={() => setActiveCategory(category.key)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      activeCategory === category.key
                        ? 'bg-green-100 border-2 border-green-300'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{category.title}</div>
                        <div className="text-xs text-gray-600 mt-1">{category.description}</div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <div
                          className={`w-2 h-2 rounded-full ${hasProgress ? 'bg-green-500' : 'bg-gray-300'}`}
                        />
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${challengeResult.color}`}
                        >
                          {challengeResult.level}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                {pestelCategories.find((cat) => cat.key === activeCategory)?.title} Factors
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {pestelCategories.find((cat) => cat.key === activeCategory)?.description}
            </p>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Evaluate each factor to determine if it represents an opportunity or threat for your
                industry.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Wider Checklist */}
        <div className="xl:col-span-3">
          {pestelCategories.map((category) => (
            <div
              key={category.key}
              className={activeCategory === category.key ? 'block h-full' : 'hidden'}
            >
              <PestelChecklist
                entityType={category.entityType}
                entityKey={category.key}
                companyId={companyId}
                title={`${category.title} Analysis`}
                onScoreChange={(score: number) => handleScoreChange(category.key, score)}
                className="h-full"
              />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
